/**
 * Authentication utilities for JWT token handling
 */

export type UserRole = 'AvacareLeadManager' | 'LMASuperAdmin';

export interface DecryptedToken {
  Role: string;
  UserName: string;
  [key: string]: any; // Allow other JWT claims
}

/**
 * Encryption key - Retrieved from environment variable
 * Falls back to default key if not set (for development)
 */
const ENCRYPTION_KEY = process.env.NEXT_PUBLIC_ENCRYPTION_KEY || 'leadManagementApp-auth-key-2024';

/**
 * Converts a string to an ArrayBuffer
 */
function stringToArrayBuffer(str: string): ArrayBuffer {
  const encoder = new TextEncoder();
  return encoder.encode(str).buffer;
}

/**
 * Derives a crypto key from a password string
 * Matches Sitecore's Rfc2898DeriveBytes: PBKDF2, 100000 iterations, SHA-256
 */
async function deriveKey(password: string): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    stringToArrayBuffer(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  const saltBuffer = stringToArrayBuffer('leadManagementApp-salt');

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-CBC', length: 256 }, // AES-CBC to match Sitecore
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Removes PKCS7 padding from decrypted data
 */
function removePKCS7Padding(data: Uint8Array): Uint8Array {
  const padding = data[data.length - 1];
  if (padding > 16 || padding === 0) {
    // Return data as-is if padding seems invalid
    return data;
  }
  return data.slice(0, data.length - padding);
}

/**
 * Encrypts a JWT token using AES-CBC (matching Sitecore)
 */
export async function encryptToken(token: string): Promise<string> {
  try {
    const key = await deriveKey(ENCRYPTION_KEY);
    const iv = crypto.getRandomValues(new Uint8Array(16)); // 128-bit IV for CBC

    // Add PKCS7 padding
    const tokenBytes = new Uint8Array(stringToArrayBuffer(token));
    const blockSize = 16;
    const padding = blockSize - (tokenBytes.length % blockSize);
    const paddedData = new Uint8Array(tokenBytes.length + padding);
    paddedData.set(tokenBytes);
    paddedData.fill(padding, tokenBytes.length);

    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-CBC',
        iv: iv,
      },
      key,
      paddedData
    );

    // Combine IV and encrypted data, then encode as base64
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);

    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt token');
  }
}

/**
 * Decrypts an encrypted token using AES-CBC (matching Sitecore)
 */
export async function decryptToken(encryptedToken: string): Promise<string> {
  try {
    const key = await deriveKey(ENCRYPTION_KEY);
    const combined = Uint8Array.from(atob(encryptedToken), (c) => c.charCodeAt(0));
    const iv = combined.slice(0, 16); // 16-byte IV for CBC
    const encrypted = combined.slice(16);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv: iv,
      },
      key,
      encrypted
    );

    // Remove PKCS7 padding and convert to string
    const unpaddedData = removePKCS7Padding(new Uint8Array(decrypted));
    const decoder = new TextDecoder();
    return decoder.decode(unpaddedData);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Failed to decrypt token');
  }
}

/**
 * Parses a JWT token and returns the payload
 * Note: This does NOT verify the signature. For production, you should verify the signature.
 */
export function parseJWT(token: string): DecryptedToken | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(paddedPayload);
    const parsed = JSON.parse(decoded);

    return parsed as DecryptedToken;
  } catch (error) {
    console.error('JWT parsing error:', error);
    return null;
  }
}

/**
 * Validates that the decrypted token has required fields, valid role, and is not expired
 */
export function validateTokenData(data: DecryptedToken | null): boolean {
  if (!data) return false;
  if (!data.Role || !data.UserName) return false;

  // Accept role if it contains "AvacareLeadManager" or "LMASuperAdmin" (handles "sitecore\RoleName" format)
  const validRoles = ['AvacareLeadManager', 'LMASuperAdmin'];
  const hasValidRole = validRoles.some((role) => data.Role.includes(role));
  if (!hasValidRole) {
    console.warn('Invalid role:', data.Role);
    return false;
  }

  // Check token expiration (exp is in seconds, Date.now() is in milliseconds)
  if (data.exp) {
    const currentTime = Math.floor(Date.now() / 1000);
    if (data.exp < currentTime) {
      console.warn('Token has expired');
      return false;
    }
  }

  return true;
}
